import type { Observable } from 'rxjs';
import type { ReactiveFormDefinition } from '../../interfaces/reactive-form-definition.interface';
import type { ReactiveChangeOrigin } from '../change-origin/reactive-change-origin';

export interface ReactiveFormFacadeConfiguration<T> {
  readonly applyReactiveDefinition: (definition: ReactiveFormDefinition<T> | null) => void;
  readonly transformPreviousDataForForm: (previousData: T | null) => T | null;
  readonly getId: () => string;
  readonly getFormType: () => number | null;
  readonly getPreviousData: () => T | null;
  readonly setPreviousData: (previousData: T | null) => void;
  readonly setNeedsDropdowns: (needsDropdowns: boolean) => void;
  readonly isBootstrapped: () => boolean;
  readonly setBootstrapped: (isBootstrapped: boolean) => void;
  readonly setPreviousDataReady: (isReady: boolean) => void;
  readonly canMutateModelSafely: () => boolean;
  readonly restoreDeclarativeDropdownCache: () => void;
  readonly initializeModel: () => void;
  readonly syncFieldRuntimeStates: () => void;
  readonly indexRepeatableBindingControls: () => void;
  readonly subscribeToFormChanges: () => void;
  readonly executeFieldsLogic: (fieldName: string | null, origin: ReactiveChangeOrigin) => Promise<void>;
  readonly setOriginalModelFromCurrent: () => void;
  readonly registerDropdownTrigger: (trigger: Observable<void> | null) => void;
  readonly loadInitialDeclarativeDropdowns: () => Promise<void>;
  readonly patchFromPreviousDataPristine: () => void;
  readonly patchRepeatableBindingsFromPreviousDataPristine: () => void;
  readonly remapDropdownFieldsPristine: () => void;
  readonly saveDeclarativeDropdownCache: () => void;
  readonly publishSilentModelSnapshot: (changedField: string, origin: ReactiveChangeOrigin) => void;
  readonly loadHydratedDeclarativeDropdownDependencies: () => Promise<void>;
  readonly updateLoadingState: () => void;
  readonly refreshRenderedFields: () => void;
  readonly markForCheck: () => void;
  readonly fetchPreviousDataById: (id: string, onResolved: (previousData: T | null) => void) => void;
}

export interface ReactiveFormPreviousDataPatchOptions {
  readonly changedField: string;
  readonly origin: ReactiveChangeOrigin;
  readonly saveCache: boolean;
  readonly refreshRenderedFields: boolean;
}

const MAX_MUTATING_FORM_TYPE = 2;
const GENERATION_FORM_TYPE = 6;

/**
 * Coordinates the generic Reactive Form Builder lifecycle.
 *
 * The Angular component owns rendering and dependency injection, while this facade owns the orchestration sequence for
 * bootstrap, previous-data hydration and dropdown refreshes. Keeping the lifecycle here makes the component easier to
 * shrink without coupling the implementation to a specific business form.
 */
export class ReactiveFormFacade<T> {
  constructor(private readonly configuration: ReactiveFormFacadeConfiguration<T>) {}

  public async bootstrap(definition: ReactiveFormDefinition<T> | null, trigger: Observable<void> | null): Promise<void> {
    this.configuration.applyReactiveDefinition(definition);
    this.updateNeedsDropdowns(trigger);
    this.configuration.setPreviousData(
      this.configuration.transformPreviousDataForForm(this.configuration.getPreviousData()),
    );

    this.configuration.restoreDeclarativeDropdownCache();
    this.configuration.initializeModel();
    this.configuration.syncFieldRuntimeStates();
    this.configuration.indexRepeatableBindingControls();
    this.configuration.subscribeToFormChanges();
    this.configuration.setOriginalModelFromCurrent();
    await this.configuration.executeFieldsLogic(null, 'programmatic');
    this.configuration.syncFieldRuntimeStates();
    this.configuration.setBootstrapped(true);
    this.configuration.registerDropdownTrigger(trigger);
    await this.configuration.loadInitialDeclarativeDropdowns();

    const id = this.configuration.getId();
    const currentPreviousData = this.configuration.getPreviousData();

    if (!id && currentPreviousData !== null) {
      this.configuration.setPreviousDataReady(true);
      await this.applyPreviousDataPatch({
        changedField: '__previous_data_patch',
        origin: 'hydrate',
        saveCache: false,
        refreshRenderedFields: false,
      });
      this.configuration.markForCheck();
      return;
    }

    if (id) {
      this.configuration.setPreviousDataReady(false);
      this.configuration.updateLoadingState();
      this.configuration.fetchPreviousDataById(id, (previousData) => {
        void this.applyFetchedPreviousData(previousData);
      });
      return;
    }

    this.configuration.setPreviousDataReady(true);
    this.configuration.updateLoadingState();
    this.configuration.markForCheck();
  }

  public handleDropdownTriggerChange(trigger: Observable<void> | null): void {
    this.updateNeedsDropdowns(trigger);

    if (this.configuration.isBootstrapped()) {
      this.configuration.registerDropdownTrigger(trigger);
    }

    this.configuration.syncFieldRuntimeStates();
    this.configuration.updateLoadingState();
    this.configuration.markForCheck();
  }

  public async handlePreviousDataInputChange(previousData: T | null | undefined): Promise<void> {
    if (previousData == null) {
      return;
    }

    this.configuration.setPreviousData(this.configuration.transformPreviousDataForForm(previousData));
    this.configuration.setPreviousDataReady(true);

    if (this.configuration.canMutateModelSafely()) {
      await this.applyPreviousDataPatch({
        changedField: '__previous_data_change',
        origin: 'hydrate',
        saveCache: false,
        refreshRenderedFields: false,
      });
    }

    this.configuration.syncFieldRuntimeStates();
    this.configuration.updateLoadingState();
    this.configuration.markForCheck();
  }

  public async refreshDropdownMappings(): Promise<void> {
    if (!this.configuration.canMutateModelSafely()) {
      return;
    }

    this.configuration.remapDropdownFieldsPristine();
    await this.configuration.executeFieldsLogic(null, 'dropdownRefresh');
    this.configuration.setOriginalModelFromCurrent();
    this.configuration.saveDeclarativeDropdownCache();
    this.configuration.syncFieldRuntimeStates();
    this.configuration.publishSilentModelSnapshot('__dropdown_refresh', 'dropdownRefresh');
    await this.configuration.loadHydratedDeclarativeDropdownDependencies();
    this.configuration.updateLoadingState();
    this.configuration.refreshRenderedFields();
  }

  public async markExternalDropdownsReady(origin: ReactiveChangeOrigin): Promise<void> {
    if (this.configuration.canMutateModelSafely()) {
      this.configuration.remapDropdownFieldsPristine();
      await this.configuration.executeFieldsLogic(null, origin);
      await this.configuration.loadHydratedDeclarativeDropdownDependencies();
      this.configuration.patchRepeatableBindingsFromPreviousDataPristine();
      this.configuration.remapDropdownFieldsPristine();
      this.configuration.setOriginalModelFromCurrent();
      this.configuration.saveDeclarativeDropdownCache();
      this.configuration.syncFieldRuntimeStates();
    }

    this.configuration.syncFieldRuntimeStates();
    this.configuration.updateLoadingState();
    this.configuration.markForCheck();
  }

  public async applyPreviousDataPatch(options: ReactiveFormPreviousDataPatchOptions): Promise<void> {
    this.configuration.patchFromPreviousDataPristine();
    await this.configuration.executeFieldsLogic(null, options.origin);
    await this.configuration.loadHydratedDeclarativeDropdownDependencies();
    this.configuration.patchRepeatableBindingsFromPreviousDataPristine();
    this.configuration.remapDropdownFieldsPristine();
    await this.configuration.executeFieldsLogic(null, options.origin);
    this.configuration.setOriginalModelFromCurrent();

    if (options.saveCache) {
      this.configuration.saveDeclarativeDropdownCache();
    }

    this.configuration.syncFieldRuntimeStates();
    this.configuration.publishSilentModelSnapshot(options.changedField, options.origin);
    this.configuration.updateLoadingState();

    if (options.refreshRenderedFields) {
      this.configuration.refreshRenderedFields();
    }
  }

  private async applyFetchedPreviousData(previousData: T | null): Promise<void> {
    this.configuration.setPreviousData(this.configuration.transformPreviousDataForForm(previousData));
    this.configuration.setPreviousDataReady(true);
    await this.applyPreviousDataPatch({
      changedField: '__previous_data_patch',
      origin: 'hydrate',
      saveCache: true,
      refreshRenderedFields: false,
    });
    this.configuration.markForCheck();
  }

  private updateNeedsDropdowns(trigger: Observable<void> | null): void {
    const formType = this.configuration.getFormType();
    const needsDropdowns = formType !== null && (formType <= MAX_MUTATING_FORM_TYPE || formType === GENERATION_FORM_TYPE) && !!trigger;

    this.configuration.setNeedsDropdowns(needsDropdowns);
  }
}
